import { AbstractClient, CompositionClient } from "@/app/client";
import { UUID } from "crypto";
import {
  PartnerPreference,
  PartnerPreferenceCreate,
  PartnerPreferenceUpdate,
  PartnerPreferenceWithProduct,
} from "../types/partnerPreferences";

export default class APIPartnerPreferences extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  /**
   * Get all preferences for a partner
   */
  getPartnerPreferences = async (
    partnerId: UUID,
  ): Promise<PartnerPreferenceWithProduct[]> => {
    let res = await this._fetch({
      url: `/partner-preferences/partners/${partnerId}/preferences`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Get only general (non-product-specific) preferences for a partner
   */
  getGeneralPreferences = async (
    partnerId: UUID,
  ): Promise<PartnerPreference[]> => {
    let res = await this._fetch({
      url: `/partner-preferences/partners/${partnerId}/preferences/general`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Get only product-specific preferences for a partner
   */
  getProductPreferences = async (
    partnerId: UUID,
  ): Promise<PartnerPreferenceWithProduct[]> => {
    let res = await this._fetch({
      url: `/partner-preferences/partners/${partnerId}/preferences/products`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Create a new preference for a partner
   */
  postPartnerPreference = async (
    partnerId: UUID,
    { ...body }: PartnerPreferenceCreate,
  ): Promise<PartnerPreference> => {
    let res = await this._fetch({
      url: `/partner-preferences/partners/${partnerId}/preferences`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Update a preference
   */
  putPreference = async (
    preferenceId: UUID,
    { ...body }: PartnerPreferenceUpdate,
  ): Promise<PartnerPreference> => {
    let res = await this._fetch({
      url: `/partner-preferences/preferences/${preferenceId}`,
      method: "PUT",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Delete a preference
   */
  deletePreference = async (preferenceId: UUID): Promise<PartnerPreference> => {
    let res = await this._fetch({
      url: `/partner-preferences/preferences/${preferenceId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Search for partners matching preference criteria
   */
  searchPartners = async (filters: {
    preference_type?: string;
    min_sensitivity?: number;
    max_sensitivity?: number;
    product_id?: UUID;
  }): Promise<UUID[]> => {
    const params = new URLSearchParams();
    if (filters.preference_type)
      params.append("preference_type", filters.preference_type);
    if (filters.min_sensitivity !== undefined)
      params.append("min_sensitivity", filters.min_sensitivity.toString());
    if (filters.max_sensitivity !== undefined)
      params.append("max_sensitivity", filters.max_sensitivity.toString());
    if (filters.product_id) params.append("product_id", filters.product_id);

    let res = await this._fetch({
      url: `/partner-preferences/search/partners?${params.toString()}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
